import { Component, useEffect, useRef } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import type { ButtonProps } from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { HostMetrics } from '@amzn/work-spaces-agentic-web-viewer';
import type { ILogger } from '@amzn/work-spaces-web-client-core-lib';
import { useSessionStore } from '@/stores/session';
import {
  AGENTIC_CRASH_OPERATION,
  VIEWER_CRASH_METRIC,
} from './agenticMetricNames';
import { ViewerChunkLoadError } from './agenticViewerChunk';
import { errorNameOf } from './errorNameOf';
import { removeWspContextHandoff } from './wspContextHandoff';

type CrashReason = 'chunk-load' | 'render';

interface AgenticRouteErrorBoundaryProps extends PropsWithChildren {
  metrics: HostMetrics;
  logger: ILogger;
}

interface AgenticRouteErrorBoundaryState {
  crashReason: CrashReason | null;
}

const crashReasonOf = (error: unknown): CrashReason =>
  error instanceof ViewerChunkLoadError ? 'chunk-load' : 'render';

const CrashPrompt: React.FC<{ crashReason: CrashReason }> = ({
  crashReason,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resetSession = useSessionStore((state) => state.resetSession);
  const actionButton = useRef<ButtonProps.Ref>(null);

  // Focus was inside the viewer that just unmounted.
  useEffect(() => {
    actionButton.current?.focus();
  }, []);

  // A chunk that failed to load used no credentials, so a reload can still
  // connect. A render crash unmounted a viewer that already spent them.
  const reloadPage = () => window.location.reload();
  const returnToSignIn = () => {
    resetSession();
    removeWspContextHandoff();
    navigate('/login');
  };
  const offersReload = crashReason === 'chunk-load';

  return (
    <div role="alert">
      <Box padding="xl" textAlign="center">
        <SpaceBetween size="m">
          <Box variant="h2">{t('ws-error')}</Box>
          <Box variant="p">{t('ws-streaming-failed-generic-error')}</Box>
          <Button
            ref={actionButton}
            variant="primary"
            onClick={offersReload ? reloadPage : returnToSignIn}
          >
            {t(offersReload ? 'ws-auth-reconnect' : 'ws-back-login')}
          </Button>
        </SpaceBetween>
      </Box>
    </div>
  );
};

// The viewer's text can hold builder content, so only error names leave here.
class AgenticRouteErrorBoundary extends Component<
  AgenticRouteErrorBoundaryProps,
  AgenticRouteErrorBoundaryState
> {
  state: AgenticRouteErrorBoundaryState = { crashReason: null };

  static getDerivedStateFromError(
    error: unknown
  ): AgenticRouteErrorBoundaryState {
    return { crashReason: crashReasonOf(error) };
  }

  componentDidCatch(error: unknown): void {
    const reason = crashReasonOf(error);
    const errorName =
      error instanceof ViewerChunkLoadError
        ? error.causeName
        : errorNameOf(error);
    this.props.metrics.emit(AGENTIC_CRASH_OPERATION, VIEWER_CRASH_METRIC, 1, {
      Reason: reason,
      Panel: 'route',
    });
    this.props.metrics.flush?.();
    this.props.logger.error(
      `Agentic viewer crashed: reason=${reason} error=${errorName}`
    );
  }

  render(): ReactNode {
    const { crashReason } = this.state;
    return crashReason === null ? (
      this.props.children
    ) : (
      <CrashPrompt crashReason={crashReason} />
    );
  }
}

export default AgenticRouteErrorBoundary;
